import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import NgwTextInput from "../../components/common/TextInput";
import CategoryCheckbox from "../../components/timeOffCategory/CategoryCheckbox";
import EmployeeRolesDropdown from "../../components/timeOffCategory/EmployeeRolesDropdown";
import CategoryColorDropdown, { categoryColors } from "../../components/timeOffCategory/CategoryColorDropdown";
import CategoryIconDropdown from "../../components/timeOffCategory/CategoryIconDropdown";
import {
    CreateTimeoffCategoryRequest,
    EmployeeRole,
    TimeoffEarningCodeType,
    UpdateTimeoffCategoryRequest,
    Workflow,
} from "../../types";
import { getWorkflowDefinition } from "../../redux/features/workflow";
import { getAllEmployeeRoles } from "../../redux/features/userv2";
import WorkflowDropdown from "../../components/timeOffCategory/WorkflowDropdown";
import TimeTrackingDropdown from "../../components/timeOffCategory/TimeTrackingDropdown";
import { createTimeoffCategory, updateTimeoffCategory } from "../../redux/features/timeoffcategory/api";
import EarningCodeDropdown from "../../components/timeOffCategory/EarningCodesDropdown";
import { getTimeoffEarningCodes } from "../../redux/features/timeoff";
import { ChevronLeft, Loader } from "lucide-react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

const AddTimeOffCategory: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Local state instead of Redux state
    const [categoryName, setCategoryName] = useState<string>("");
    const [categoryCode, setCategoryCode] = useState<string>("");
    const [isPaidPolicy, setIsPaidPolicy] = useState<boolean>(false);
    const [isPublic, setIsPublic] = useState<boolean>(false);
    const [calendarColor, setCalendarColor] = useState<string>(categoryColors[0].className);
    const [icon, setIcon] = useState<string | null>(null);
    // const [trackTime, setTrackTime] = useState<"Days" | "Hours" | null>(null);
    const [visibilityOption, setVisibilityOption] = useState<string>("");

    const { workflows, isLoadingWorkflows } = useSelector((state: RootState) => state.workflow);
    const { allEmployeeRoles, isLoadingAllEmployeeRoles } = useSelector((state: RootState) => state.userV2);
    const { isLoadingTimeoffEarningCodes, timeoffEarningCodes } = useSelector((state: RootState) => state.timeoff);

    const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
    const [selectedRoles, setSelectedRoles] = useState<EmployeeRole[]>([]);
    const [selectedEarningCode, setSelectedEarningCode] = useState<TimeoffEarningCodeType | null>(null);
    const [selectedTimeTracking, setSelectedTimeTracking] = useState<"Days" | "Hours" | null>(null);

    const { isCategoryEditMode, selectedCategoryToEdit, isAddingCategory } = useSelector(
        (state: RootState) => state.timeoffCategory
    );

    useEffect(() => {
        if (isCategoryEditMode) {
            setIsPaidPolicy(selectedCategoryToEdit?.is_paid_policy === "1");
            setIsPublic(selectedCategoryToEdit?.is_publicly_visible === "1");
        }
    }, [isCategoryEditMode, selectedCategoryToEdit]);

    useEffect(() => {
        dispatch(getWorkflowDefinition({}));
        dispatch(getAllEmployeeRoles({ isActive: "1" }));
        dispatch(getTimeoffEarningCodes());
    }, [dispatch]);

    const handleSave = async () => {
        if (isCategoryEditMode) {
            // const visibleToJson: VisibleToJsonType = {
            //     jobRole: selectedRoles.length > 0 ? selectedRoles : selectedCategoryToEdit?.visible_to_json?.jobRole,
            // };
            const request: UpdateTimeoffCategoryRequest = {
                time_off_category_id: selectedCategoryToEdit?.time_off_categories_id,
                category_name:
                    categoryName.length > 0 ? categoryName : selectedCategoryToEdit?.category_name || categoryName,
                calendar_color:
                    categoryCode.length > 0 ? calendarColor : selectedCategoryToEdit?.calendar_color || calendarColor,
                icon: icon != null && icon.length > 0 ? icon : selectedCategoryToEdit?.icon || icon,
                track_time_in:
                    selectedTimeTracking != null && selectedTimeTracking.length > 0
                        ? selectedTimeTracking
                        : selectedCategoryToEdit?.track_time_in || selectedTimeTracking,
                workflow_code:
                    selectedWorkflow != null ? selectedWorkflow.wf_code : selectedCategoryToEdit?.workflow_code,
                category_code: categoryCode || selectedCategoryToEdit?.category_code,
                paid_policy_status: isPaidPolicy ? "1" : "0",
                publicly_visible_status: isPublic ? "1" : "0",
                earning_code:
                    selectedEarningCode != null
                        ? selectedEarningCode.earnings_code
                        : selectedCategoryToEdit?.earning_code,
            };

            const response = await dispatch(updateTimeoffCategory(request)).unwrap();
            if (response.status) {
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } else {
            const request: CreateTimeoffCategoryRequest = {
                category_name: categoryName,
                calendar_color: calendarColor,
                icon: icon,
                track_time_in: selectedTimeTracking,
                workflow_code: selectedWorkflow?.wf_code,
                category_code: categoryCode,
                paid_policy_status: isPaidPolicy ? "1" : "0",
                publicly_visible_status: isPublic ? "1" : "0",
                earning_code: selectedEarningCode?.earnings_code,
            };

            const response = await dispatch(createTimeoffCategory(request)).unwrap();

            if (response.status) {
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        }

        navigate(-1);
    };

    return (
        <div className="flex flex-col h-full overflow-y-auto">
            <div className="bg-white p-4 w-full flex flex-col">
                <div className="flex-grow p-2">
                    <div className="flex items-center mb-4">
                        <button
                            onClick={() => window.history.back()}
                            className="text-[#3788d8] hover:text-[#2a6ebb] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2a6ebb]"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <h3 className="text-xl font-bold text-[#3788d8] ml-4">
                            {isCategoryEditMode ? "Update Time Off Category" : "Add Time Off Category"}
                        </h3>
                    </div>

                    <NgwTextInput
                        label="Category Name*"
                        value={isCategoryEditMode && !categoryName ? selectedCategoryToEdit?.category_name || "" : categoryName}
                        onChange={(val) => setCategoryName(val)}
                        className="w-full sm:w-2/3"
                    />

                    <NgwTextInput
                        label="Category code*"
                        value={isCategoryEditMode && !categoryCode ? selectedCategoryToEdit?.category_code || "" : categoryCode}
                        onChange={(val) => setCategoryCode(val)}
                        className="w-full sm:w-2/3"
                    />

                    <p className="text-gray-500 text-sm mb-4">
                        People will see this name when requesting time off for the policies in this Category.
                    </p>

                    <CategoryCheckbox
                        label="Time off policies in this Category are paid policies"
                        checked={isPaidPolicy}
                        onChange={() => setIsPaidPolicy(!isPaidPolicy)}
                    />

                    <CategoryCheckbox
                        label="Make this Category name publicly visible on the calendar"
                        checked={isPublic}
                        onChange={() => setIsPublic(!isPublic)}
                    />

                    {isPublic && (
                        <div className="mt-4">
                            <p className="font-semibold text-gray-700">Allow it to be seen by...</p>
                            <div className="mt-2">
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        name="visibility"
                                        value="everyone"
                                        checked={visibilityOption === "everyone"}
                                        onChange={() => setVisibilityOption("everyone")}
                                    />
                                    <span>Everyone</span>
                                </label>

                                <label className="flex items-center space-x-2 mt-2">
                                    <input
                                        type="radio"
                                        name="visibility"
                                        value="accessLevels"
                                        checked={visibilityOption === "accessLevels"}
                                        onChange={() => setVisibilityOption("accessLevels")}
                                    />
                                    <span>Only certain access levels</span>
                                </label>
                            </div>
                        </div>
                    )}

                    {isPublic && visibilityOption === "accessLevels" && (
                        <div className="mt-4">
                            <EmployeeRolesDropdown
                                label="Choose which access level(s) can see it..."
                                selectedRoles={selectedRoles}
                                availableRoles={allEmployeeRoles}
                                onSelectionChange={setSelectedRoles}
                                isLoading={isLoadingAllEmployeeRoles}
                            />
                        </div>
                    )}

                    <hr className="my-4" />

                    <div className="mt-4">
                        <WorkflowDropdown
                            label="Workflow"
                            selectedWorkflow={
                                isCategoryEditMode && !selectedWorkflow
                                    ? workflows.find((wf) => wf.wf_code === selectedCategoryToEdit?.workflow_code) || null
                                    : selectedWorkflow
                            }
                            availableWorkflows={workflows}
                            onSelectionChange={setSelectedWorkflow}
                            isLoading={isLoadingWorkflows}
                        />
                    </div>

                    <hr className="my-4" />

                    <div className="mt-4">
                        <TimeTrackingDropdown
                            label="Time Tracking"
                            onSelectionChange={setSelectedTimeTracking}
                            selectedOption={
                                isCategoryEditMode && !selectedTimeTracking
                                    ? (["Days", "Hours"].includes(selectedCategoryToEdit?.track_time_in || "")
                                        ? (selectedCategoryToEdit?.track_time_in as "Days" | "Hours")
                                        : null)
                                    : selectedTimeTracking
                            }
                        />
                    </div>

                    <div className="mt-4">
                        <EarningCodeDropdown
                            label="Earning code"
                            onSelectionChange={setSelectedEarningCode}
                            selectedEarningCode={
                                isCategoryEditMode && !selectedEarningCode
                                    ? timeoffEarningCodes.find((code) => code.earnings_code === selectedCategoryToEdit?.earning_code) || null
                                    : selectedEarningCode
                            }
                            earningCodes={timeoffEarningCodes}
                            isLoading={isLoadingTimeoffEarningCodes}
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-8 mt-4">
                        <div>
                            <CategoryIconDropdown onSelect={(icon) => setIcon(icon)} />
                        </div>
                        <CategoryColorDropdown
                            selectedColor={calendarColor}
                            onSelect={(color) => setCalendarColor(color)}
                        />
                    </div>
                </div>

                <div className="flex justify-between p-2 mt-4 border-t">
                    <button className="text-gray-500 hover:underline px-4 py-2">Cancel</button>
                    {isAddingCategory ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader size={24} className="w-6 h-6 animate-spin text-gray-500" />
                        </div>
                    ) : (
                        <button className="bg-[#3788d8] text-white px-6 py-2 rounded-lg shadow" onClick={handleSave}>
                            Save
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddTimeOffCategory;
